import axios from "axios";
import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import { getSessionFromCookie } from "../utils/cognito";
import { BillingInfo } from "../../types/addServiceTypes";
import * as IBAN from "iban";
import { RootState } from "../../store/indexRedux";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import { updateCompanyEmail } from "../../store/companySlice";
pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

interface DigitalSignatureModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSign: (signatureName: string, billingInfo: BillingInfo) => Promise<void>;
  signerName?: string;
  pdfUrls?: {
    offtake: string | null;
    injection: string | null;
  };
  ean: string;
  companyId: number;
  direction?: "offtake" | "injection" | "combined";
  contractError?: string | null;
  hasValidIBAN?: boolean;
  billingInfo: BillingInfo | null;
}

export const DigitalSignatureModal = ({
  isOpen,
  onClose,
  onSign,
  signerName = "JOHN DOE",
  pdfUrls,
  ean,
  companyId,
  direction,
  contractError,
  billingInfo,
}: DigitalSignatureModalProps) => {
  const { t } = useTranslation("addservice");
  const [signatureName, setSignatureName] = useState("");
  const [signeeFunction, setSigneeFunction] = useState("");
  const [signatureMode, setSignatureMode] = useState<"type" | "draw" | "upload">("type");
  const [canvasHasDrawing, setCanvasHasDrawing] = useState(false);
  const [uploadedSignature, setUploadedSignature] = useState<string | null>(null);
  const [isSignatureProcessing, setIsSignatureProcessing] = useState(false);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasScrolledToEnd, setHasScrolledToEnd] = useState(false);
  const [hasViewedAllPages, setHasViewedAllPages] = useState(false);
  const [documentsLoaded, setDocumentsLoaded] = useState({ offtake: false, injection: false });
  const [documentPages, setDocumentPages] = useState({ offtake: 0, injection: 0 });
  const [hasAcceptedTerms, setHasAcceptedTerms] = useState(false);
  const [pdfLoadError, setPdfLoadError] = useState<string | null>(null);
  const [showBillingEdit, setShowBillingEdit] = useState(false);
  const [ibanValid, setIbanValid] = useState<boolean | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const viewedPagesRef = useRef<Set<number>>(new Set());
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const isDrawingRef = useRef(false);
  // The pointer that owns the in-progress stroke, so concurrent pointers (a second finger,
  // palm) can be ignored instead of hijacking or prematurely ending the signature.
  const activePointerIdRef = useRef<number | null>(null);

  const selectedCompany = useSelector((state: RootState) => state.selectedCompany.selectedUserCompany);
  const user = useSelector((state: RootState) => state.user);
  const isAdmin = user?.user?.role === "admin";
  console.log(selectedCompany?.companyEmail);
  const [companyEmail, setCompanyEmail] = useState(selectedCompany?.companyEmail || "");

  const dispatch = useDispatch();

  console.log("document loaded status:", documentsLoaded);
  console.log("pdf loaded error", pdfLoadError);

  // Billing form state
  const [editableBillingInfo, setEditableBillingInfo] = useState<BillingInfo>({
    billing_id: billingInfo?.billing_id || 0,
    company_id: billingInfo?.company_id || companyId,
    billingEmail: billingInfo?.billingEmail || "",
    iban: billingInfo?.iban || "",
    poNumber: billingInfo?.poNumber || null,
    twikeyRef: billingInfo?.twikeyRef || "",
    active_dd_mandate: billingInfo?.active_dd_mandate || 0,
    eol_dd_mandate_uploaded: billingInfo?.eol_dd_mandate_uploaded || 0,
    twikeyDate: billingInfo?.twikeyDate || null,
    twikeyCode: billingInfo?.twikeyCode || null,
    twikeyValid: billingInfo?.twikeyValid || 0,
    billingAddressStreet: billingInfo?.billingAddressStreet || "",
    billingAddressNumber: billingInfo?.billingAddressNumber || "",
    billingAddressZip: billingInfo?.billingAddressZip || "",
    billingAddressCity: billingInfo?.billingAddressCity || "",
    billingAddressCountry: billingInfo?.billingAddressCountry || "Belgium",
    invoicingCycle: billingInfo?.invoicingCycle || "monthly",
    created_at: billingInfo?.created_at || new Date().toISOString(),
    updated_at: billingInfo?.updated_at || new Date().toISOString(),
  });

  // Update billing info when props change
  useEffect(() => {
    if (billingInfo) {
      setEditableBillingInfo({
        billing_id: billingInfo.billing_id || 0,
        company_id: billingInfo.company_id || companyId,
        billingEmail: billingInfo.billingEmail || "",
        iban: billingInfo.iban || "",
        poNumber: billingInfo.poNumber || null,
        twikeyRef: billingInfo.twikeyRef || "",
        active_dd_mandate: billingInfo.active_dd_mandate || 0,
        eol_dd_mandate_uploaded: billingInfo.eol_dd_mandate_uploaded || 0,
        twikeyDate: billingInfo.twikeyDate || null,
        twikeyCode: billingInfo.twikeyCode || null,
        twikeyValid: billingInfo.twikeyValid || 0,
        billingAddressStreet: billingInfo.billingAddressStreet || "",
        billingAddressNumber: billingInfo.billingAddressNumber || "",
        billingAddressZip: billingInfo.billingAddressZip || "",
        billingAddressCity: billingInfo.billingAddressCity || "",
        billingAddressCountry: billingInfo.billingAddressCountry || "Belgium",
        invoicingCycle: billingInfo.invoicingCycle || "monthly",
        created_at: billingInfo.created_at || new Date().toISOString(),
        updated_at: billingInfo.updated_at || new Date().toISOString(),
      });
      // Validate existing IBAN
      if (billingInfo.iban) {
        validateIBAN(billingInfo.iban);
      }
    }
  }, [billingInfo, companyId]);

  const validateIBAN = (value: string) => {
    const cleanIBAN = value.replace(/\s/g, "").toUpperCase();
    if (!cleanIBAN) {
      setIbanValid(null);
      return;
    }
    setIbanValid(IBAN.isValid(cleanIBAN));
  };

  const formatIBAN = (value: string) => {
    const cleanValue = value.replace(/\s/g, "").toUpperCase();
    const formatted = cleanValue.match(/.{1,4}/g)?.join(" ") || cleanValue;
    return formatted;
  };

  const handleBillingInputChange = (field: keyof BillingInfo, value: string) => {
    if (field === "iban") {
      value = formatIBAN(value);
      validateIBAN(value);
    }
    setEditableBillingInfo((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // Pointer Events unify mouse, touch and pen so a customer can finger-sign on a
  // touchscreen. setPointerCapture keeps the stroke flowing when the pointer leaves the
  // canvas bounds, which replaces the old document-level mousemove/mouseup listeners.
  const startDrawing = (e: React.PointerEvent<HTMLCanvasElement>) => {
    // Only the PRIMARY contact (left mouse / first finger, button === 0) starts a stroke,
    // and only when none is already active — a second finger or a right/middle button must
    // not hijack the in-progress signature.
    if (isDrawingRef.current || e.button !== 0) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    isDrawingRef.current = true;
    activePointerIdRef.current = e.pointerId;
    canvas.setPointerCapture(e.pointerId);
    const rect = canvas.getBoundingClientRect();
    ctx.beginPath();
    ctx.moveTo(
      (e.clientX - rect.left) * (canvas.width / rect.width),
      (e.clientY - rect.top) * (canvas.height / rect.height),
    );
  };

  const handleMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
    // Only the pointer that started the stroke draws, and only while its primary
    // button/contact is held (buttons === 1 for a finger down or left-mouse held) — a
    // right/middle-button drag, or a stray move after release, must not add ink.
    if (!isDrawingRef.current || e.pointerId !== activePointerIdRef.current || e.buttons !== 1) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const rect = canvas.getBoundingClientRect();
    ctx.lineTo(
      (e.clientX - rect.left) * (canvas.width / rect.width),
      (e.clientY - rect.top) * (canvas.height / rect.height),
    );
    ctx.strokeStyle = "#04344d";
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.stroke();
    setCanvasHasDrawing(true);
  };

  const stopDrawing = (e: React.PointerEvent<HTMLCanvasElement>) => {
    // Only the active pointer ends the stroke; a second finger lifting must leave it running.
    if (!isDrawingRef.current || e.pointerId !== activePointerIdRef.current) return;
    isDrawingRef.current = false;
    activePointerIdRef.current = null;
    const canvas = canvasRef.current;
    if (canvas?.hasPointerCapture(e.pointerId)) canvas.releasePointerCapture(e.pointerId);
  };

  useEffect(() => {
    // A mode switch remounts a fresh blank <canvas>; the drawn bitmap cannot survive
    // unmount, so a stale canvasHasDrawing would pass validation on an empty canvas
    // and submit a blank signature. Reset the flag on every mode change.
    setCanvasHasDrawing(false);
  }, [signatureMode]);

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.getContext("2d")?.clearRect(0, 0, canvas.width, canvas.height);
    setCanvasHasDrawing(false);
  };

  const buildSignatureImage = (): string | null => {
    if (signatureMode === "draw") {
      return canvasRef.current?.toDataURL("image/png") ?? null;
    }
    if (signatureMode === "upload") {
      return uploadedSignature;
    }
    const offscreen = document.createElement("canvas");
    offscreen.width = 300;
    offscreen.height = 80;
    const ctx = offscreen.getContext("2d");
    if (!ctx) return null;
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, 300, 80);
    ctx.font = "italic 28px Georgia, serif";
    ctx.fillStyle = "#04344d";
    ctx.textBaseline = "middle";
    ctx.fillText(signatureName.trim() || signerName, 16, 40);
    return offscreen.toDataURL("image/png");
  };

  const signatureValid =
    signatureMode === "draw" ? canvasHasDrawing : signatureMode === "upload" ? uploadedSignature !== null : true;

  // Determine which PDFs to show based on direction prop
  const getActivePdfUrls = () => {
    if (!pdfUrls) return { offtake: null, injection: null };
    if (direction === "offtake") {
      return { offtake: pdfUrls.offtake, injection: null };
    } else if (direction === "injection") {
      return { offtake: null, injection: pdfUrls.injection };
    } else {
      return pdfUrls;
    }
  };

  const activePdfUrls = getActivePdfUrls();
  const hasValidPdfs = !!(activePdfUrls.offtake || activePdfUrls.injection);

  // Reset states when modal opens/closes
  useEffect(() => {
    if (isOpen && hasValidPdfs) {
      // Reset all states when modal opens with valid PDFs
      setHasScrolledToEnd(false);
      setHasViewedAllPages(false);
      setCurrentPage(1);
      setTotalPages(0);
      setDocumentsLoaded({ offtake: false, injection: false });
      setDocumentPages({ offtake: 0, injection: 0 });
      setHasAcceptedTerms(false);
      setSignatureName("");
      setSigneeFunction("");
      setSignatureMode("type");
      setCanvasHasDrawing(false);
      setUploadedSignature(null);
      setPdfLoadError(null);
      viewedPagesRef.current.clear();
    } else if (isOpen && !hasValidPdfs) {
      setPdfLoadError(t("signingPage.commodityNoPdf"));
    }

    return () => {
      if (!isOpen) {
        // Clear states when modal closes
        setDocumentsLoaded({ offtake: false, injection: false });
        setDocumentPages({ offtake: 0, injection: 0 });
        setTotalPages(0);
        setPdfLoadError(null);
      }
    };
  }, [isOpen, hasValidPdfs, pdfUrls, direction, t]);

  const handleScroll = () => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const { scrollTop, scrollHeight, clientHeight } = container;
    if (scrollHeight - scrollTop - clientHeight < 10) {
      setHasScrolledToEnd(true);
    }

    if (totalPages > 0) {
      const pageHeight = scrollHeight / totalPages;
      const visiblePage = Math.ceil((scrollTop + clientHeight / 2) / pageHeight);
      setCurrentPage(Math.min(visiblePage, totalPages));
      viewedPagesRef.current.add(visiblePage);
      if (viewedPagesRef.current.size === totalPages) {
        setHasViewedAllPages(true);
      }
    }
  };

  const handleAcceptTermsChange = (checked: boolean) => {
    setHasAcceptedTerms(checked);
    if (checked && scrollContainerRef.current) {
      scrollContainerRef.current.scrollTo({
        top: scrollContainerRef.current.scrollHeight,
        behavior: "smooth",
      });
      setTimeout(() => {
        setHasScrolledToEnd(true);
        setHasViewedAllPages(true);
        for (let i = 1; i <= totalPages; i++) {
          viewedPagesRef.current.add(i);
        }
      }, 500);
    }
  };

  const onDocumentLoadSuccess = (type: "offtake" | "injection", { numPages }: { numPages: number }) => {
    setDocumentPages((prev) => ({ ...prev, [type]: numPages }));
    setDocumentsLoaded((prev) => ({ ...prev, [type]: true }));
    setTotalPages((prev) => prev + numPages);

    if ((type === "offtake" && !activePdfUrls.injection) || (type === "injection" && !activePdfUrls.offtake)) {
      viewedPagesRef.current.add(1);
    }

    setTimeout(() => {
      const container = scrollContainerRef.current;
      if (container && container.scrollHeight <= container.clientHeight) {
        setHasScrolledToEnd(true);
        setHasViewedAllPages(true);
      }
    }, 100);

    setPdfLoadError(null);
  };

  const onDocumentLoadError = (error: Error) => {
    console.error("Error loading PDF:", error);
    setPdfLoadError(t("signingPage.commodityPdfLoadError"));
  };

  const handleDigitalSignature = async () => {
    if (!signatureName.trim()) {
      alert(t("signingPage.commodityNameRequired"));
      return;
    }

    if (!signeeFunction.trim()) {
      alert(t("signingPage.commodityFunctionRequired"));
      return;
    }

    if (!signatureValid) {
      alert(t("signingPage.commoditySignatureRequired"));
      return;
    }

    if (!hasAcceptedTerms) {
      alert(t("signingPage.commodityTermsRequired"));
      return;
    }

    if (!hasScrolledToEnd && !hasViewedAllPages) {
      alert(t("signingPage.commodityReviewRequired"));
      return;
    }

    if (!ibanValid && !isAdmin) {
      alert(t("signingPage.commodityIbanInvalid"));
      return;
    }

    if (!editableBillingInfo.billingEmail) {
      alert(t("signingPage.commodityBillingEmailRequired"));
      return;
    }

    setIsSignatureProcessing(true);

    try {
      const session = getSessionFromCookie();
      const token = session?.getAccessToken().getJwtToken();

      const payload = {
        ean,
        name: signatureName,
        company_id: companyId,
        direction: direction || "combined",
        billingInfo: editableBillingInfo,
        companyEmail: companyEmail,
        ...(signeeFunction.trim() ? { signee_function: signeeFunction.trim() } : {}),
        signature_image: buildSignatureImage(),
      };

      const response = await axios.post(
        `${import.meta.env.VITE_API_SERVICES}/api/v1/contracts/generateContract/sign`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data) {
        dispatch(updateCompanyEmail(companyEmail));

        await onSign(signatureName, editableBillingInfo);
        setSignatureName("");
        setHasAcceptedTerms(false);
      }
    } catch (error) {
      console.error("Error processing digital signature:", error);
      if (axios.isAxiosError(error)) {
        alert(error.response?.data?.message || t("signingPage.submitError"));
      } else {
        alert(t("signingPage.submitError"));
      }
    } finally {
      setIsSignatureProcessing(false);
    }
  };

  const handleDownload = async (type: "offtake" | "injection" | "both") => {
    try {
      if (type === "both") {
        // Download both PDFs
        if (activePdfUrls.offtake) {
          const link = document.createElement("a");
          link.href = activePdfUrls.offtake;
          link.download = `offtake-contract-${ean}.pdf`;
          link.target = "_blank";
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }

        // Small delay between downloads
        setTimeout(() => {
          if (activePdfUrls.injection) {
            const link = document.createElement("a");
            link.href = activePdfUrls.injection;
            link.download = `injection-contract-${ean}.pdf`;
            link.target = "_blank";
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
          }
        }, 500);
      } else {
        const url = type === "offtake" ? activePdfUrls.offtake : activePdfUrls.injection;
        if (url) {
          const link = document.createElement("a");
          link.href = url;
          link.download = `${type}-contract-${ean}.pdf`;
          link.target = "_blank";
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }
      }
    } catch (error) {
      console.error("Error downloading PDF:", error);
      // Fallback to opening in new tab
      const url = type === "offtake" ? activePdfUrls.offtake : type === "injection" ? activePdfUrls.injection : null;
      if (url) window.open(url, "_blank");
    }
  };

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  if (!isOpen) return null;

  if (contractError) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-8 flex flex-col items-center">
          <svg className="w-12 h-12 text-red-600 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <h2 className="text-xl font-semibold text-red-700 mb-2">{t("signingPage.commodityErrorTitle")}</h2>
          <p className="text-center text-red-600 text-base mb-6">{contractError}</p>
          <button
            onClick={onClose}
            className="px-6 py-2 rounded bg-primary-600 text-white font-semibold hover:bg-primary-700"
          >
            {t("signingPage.commodityClose")}
          </button>
        </div>
      </div>
    );
  }

  const contractTypeLabel =
    direction === "combined"
      ? t("signingPage.commodityCombined")
      : direction === "offtake"
        ? t("signingPage.commodityOfftakeContract")
        : t("signingPage.commodityInjectionContract");

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-6xl h-[92vh] flex">
        {/* Left Panel - PDF Viewer (70% width) */}
        <div className="flex-1 flex flex-col border-r border-gray-200">
          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-800">{contractTypeLabel}</h2>
                <p className="text-xs text-gray-500 mt-1">
                  {totalPages > 0 && t("signingPage.commodityPageOf", { current: currentPage, total: totalPages })}
                  {!hasViewedAllPages && (
                    <span className="ml-2 text-secondary-600 font-medium">{t("signingPage.commodityNotReviewed")}</span>
                  )}
                  {hasViewedAllPages && (
                    <span className="ml-2 text-primary-600 font-medium">✓ {t("signingPage.commodityReviewedByUser")}</span>
                  )}
                </p>
              </div>
              <div className="flex items-center gap-2">
                {/* Download buttons */}
                {hasValidPdfs && (
                  <>
                    {activePdfUrls.offtake && (
                      <button
                        onClick={() => handleDownload("offtake")}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors"
                        title={t("signingPage.commodityDownloadOfftake")}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10"
                          />
                        </svg>
                        {t("signingPage.commodityOfftake")}
                      </button>
                    )}

                    {activePdfUrls.injection && (
                      <button
                        onClick={() => handleDownload("injection")}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors"
                        title={t("signingPage.commodityDownloadInjection")}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10"
                          />
                        </svg>
                        {t("signingPage.commodityInjection")}
                      </button>
                    )}
                  </>
                )}

                {/* <button
                  onClick={onClose}
                  className="p-1.5 text-primary-400 hover:text-primary-600 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button> */}
              </div>
            </div>
          </div>

          {/* PDF Content */}
          <div className="flex-1 overflow-hidden bg-gray-50 p-4">
            {hasValidPdfs ? (
              <div
                ref={scrollContainerRef}
                onScroll={handleScroll}
                className="h-full overflow-y-auto bg-white rounded-lg shadow-inner p-4"
              >
                {activePdfUrls.offtake && (
                  <>
                    {/* Offtake Document Label */}
                    <div className="mb-4 py-3 px-4 bg-primary-50 border-l-4 border-primary-600 rounded">
                      <h3 className="text-lg font-semibold text-primary-700">{t("signingPage.commodityOfftakeDocument")}</h3>
                    </div>

                    <Document
                      file={activePdfUrls.offtake}
                      onLoadSuccess={(info) => onDocumentLoadSuccess("offtake", info)}
                      onLoadError={onDocumentLoadError}
                      loading={
                        <div className="flex justify-center items-center h-96">
                          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
                        </div>
                      }
                      className="flex flex-col items-center"
                    >
                      {documentPages.offtake > 0 &&
                        Array.from(new Array(documentPages.offtake), (_, index) => (
                          <div key={`offtake_page_${index + 1}`} className="mb-4">
                            <Page
                              pageNumber={index + 1}
                              width={Math.min(700, window.innerWidth - 450)}
                              renderTextLayer={false}
                              renderAnnotationLayer={false}
                              className="shadow-lg"
                            />
                          </div>
                        ))}
                    </Document>
                  </>
                )}

                {activePdfUrls.injection && (
                  <>
                    {/* Separator if both documents exist */}
                    {activePdfUrls.offtake && <div className="my-8 border-b-2 border-gray-300 w-full"></div>}

                    {/* Injection Document Label */}
                    <div className="mb-4 py-3 px-4 bg-secondary-50 border-l-4 border-secondary-600 rounded">
                      <h3 className="text-lg font-semibold text-secondary-700">{t("signingPage.commodityInjectionDocument")}</h3>
                    </div>

                    <Document
                      file={activePdfUrls.injection}
                      onLoadSuccess={(info) => onDocumentLoadSuccess("injection", info)}
                      onLoadError={onDocumentLoadError}
                      loading={
                        <div className="flex justify-center items-center h-96">
                          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
                        </div>
                      }
                      className="flex flex-col items-center"
                    >
                      {documentPages.injection > 0 &&
                        Array.from(new Array(documentPages.injection), (_, index) => (
                          <div key={`injection_page_${index + 1}`} className="mb-4">
                            <Page
                              pageNumber={index + 1}
                              width={Math.min(700, window.innerWidth - 450)}
                              renderTextLayer={false}
                              renderAnnotationLayer={false}
                              className="shadow-lg"
                            />
                          </div>
                        ))}
                    </Document>
                  </>
                )}
              </div>
            ) : (
              <div className="h-full flex items-center justify-center">
                <div className="text-center">
                  <svg
                    className="w-16 h-16 text-gray-300 mx-auto mb-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                  <p className="text-gray-500">{t("signingPage.commodityLoadingContract")}</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Right Panel - Billing & Signature (30% width) */}
        <div className="w-[420px] flex justify-between flex-col bg-gray-50">
          <div>
            <div className="p-4 border-b border-gray-200 bg-white">
              <div className="flex justify-between mr-2">
                <div></div>
                <button
                  onClick={onClose}
                  className="p-1.5 text-primary-400 hover:text-primary-600 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="flex items-center mb-3">
                <h3 className="text-sm font-semibold text-primary-700 flex items-center gap-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                    />
                  </svg>
                  {t("signingPage.commodityCompanyEmailLabel")}
                  {companyEmail && validateEmail(companyEmail) && <span className="text-green-600 text-xs">✓</span>}
                </h3>
              </div>
              <input
                type="email"
                value={companyEmail}
                onChange={(e) => setCompanyEmail(e.target.value)}
                className={`w-full px-2 py-1.5 border rounded text-sm focus:border-primary-500 focus:outline-none transition-colors text-primary-600`}
                placeholder="company@example.com"
              />
              {companyEmail && !validateEmail(companyEmail) && (
                <p className="text-xs text-red-600 mt-1">{t("signingPage.commodityEmailInvalid")}</p>
              )}
            </div>
            {/* Billing Section */}
            <div className="p-4 border-b border-gray-200 bg-white">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-primary-700 flex items-center gap-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
                    />
                  </svg>
                  {t("signingPage.commodityBilling")}
                  {ibanValid && <span className="text-green-600 text-xs">✓</span>}
                </h3>
                <button
                  onClick={() => setShowBillingEdit(!showBillingEdit)}
                  className="text-xs text-primary-600 hover:text-primary-700 font-medium"
                >
                  {showBillingEdit ? t("signingPage.commodityDone") : t("signingPage.commodityEdit")}
                </button>
              </div>

              {showBillingEdit ? (
                <div className="space-y-2">
                  <div>
                    <label className="block text-xs font-medium text-primary-600 mb-1">{t("signingPage.commodityBillingEmail")}</label>
                    <input
                      type="email"
                      value={editableBillingInfo.billingEmail}
                      onChange={(e) => handleBillingInputChange("billingEmail", e.target.value)}
                      className="w-full px-2 py-1.5 border border-primary-200 rounded text-sm focus:border-primary-500 focus:outline-none transition-colors text-primary-600"
                      placeholder="billing@company.com"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-primary-600 mb-1">{t("signingPage.commodityIbanLabel")}</label>
                    <input
                      type="text"
                      value={editableBillingInfo.iban}
                      onChange={(e) => handleBillingInputChange("iban", e.target.value)}
                      className={`w-full text-primary-600 px-2 py-1.5 border border-primary-200 rounded text-sm focus:border-primary-500 focus:outline-none transition-colors${
                        editableBillingInfo.iban && !ibanValid
                          ? "border-red-300 focus:ring-red-500"
                          : "border-primary-200 focus:ring-primary-500"
                      }`}
                      placeholder="BE00 0000 0000 0000"
                    />
                    {editableBillingInfo.iban && ibanValid === false && (
                      <p className="text-xs text-red-600 mt-1">{t("signingPage.commodityIbanInvalidFormat")}</p>
                    )}
                    {editableBillingInfo.iban && ibanValid === true && (
                      <p className="text-xs text-primary-600 mt-1">✓ {t("signingPage.commodityValidIban")}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-primary-600 mb-1">{t("signingPage.commodityStreetAddress")}</label>
                    <div className="grid grid-cols-2 gap-2">
                      <input
                        type="text"
                        value={editableBillingInfo.billingAddressStreet}
                        onChange={(e) => handleBillingInputChange("billingAddressStreet", e.target.value)}
                        className="w-full text-primary-600 px-2 py-1.5 border border-primary-200 rounded text-sm focus:border-primary-500 focus:outline-none transition-colors"
                        placeholder={t("signingPage.commodityStreet")}
                      />
                      <input
                        type="text"
                        value={editableBillingInfo.billingAddressNumber}
                        onChange={(e) => handleBillingInputChange("billingAddressNumber", e.target.value)}
                        className="w-full text-primary-600 px-2 py-1.5 border border-primary-200 rounded text-sm focus:border-primary-500 focus:outline-none transition-colors"
                        placeholder={t("signingPage.commodityNumber")}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-primary-600 mb-1">{t("signingPage.commodityCityPostal")}</label>
                    <div className="grid grid-cols-3 gap-2">
                      <input
                        type="text"
                        value={editableBillingInfo.billingAddressCity}
                        onChange={(e) => handleBillingInputChange("billingAddressCity", e.target.value)}
                        className="col-span-2 text-primary-600 px-2 py-1.5 border border-primary-200 rounded text-sm focus:border-primary-500 focus:outline-none transition-colors"
                        placeholder={t("signingPage.commodityCity")}
                      />
                      <input
                        type="text"
                        value={editableBillingInfo.billingAddressZip}
                        onChange={(e) => handleBillingInputChange("billingAddressZip", e.target.value)}
                        className="px-2 py-1.5 text-primary-600 border border-primary-200 rounded text-sm focus:border-primary-500 focus:outline-none transition-colors"
                        placeholder={t("signingPage.commodityZip")}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-primary-600 mb-1">{t("signingPage.commodityInvoicingCycle")}</label>
                    <select
                      value={editableBillingInfo.invoicingCycle}
                      onChange={(e) => handleBillingInputChange("invoicingCycle", e.target.value)}
                      className="w-full text-primary-600 px-2 py-1.5 border border-primary-200 rounded text-sm focus:border-primary-500 focus:outline-none transition-colors"
                    >
                      <option value="monthly">{t("signingPage.commodityMonthly")}</option>
                      <option value="quarterly" disabled>
                        {t("signingPage.commodityQuarterly")}
                      </option>
                      <option value="yearly" disabled>
                        {t("signingPage.commodityYearly")}
                      </option>
                    </select>
                  </div>

                  <div>
                    <button
                      onClick={() => setShowBillingEdit(!showBillingEdit)}
                      className="w-full mt-3 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg font-medium text-sm transition-colors"
                    >
                      {showBillingEdit ? t("signingPage.commodityDone") : t("signingPage.commodityEdit")}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="space-y-1 text-xs text-primary-600 font-medium">
                  <p>
                    <span className="font-medium">{t("signingPage.commodityEmail")}:</span> {editableBillingInfo.billingEmail || "—"}
                  </p>
                  <p>
                    <span className="font-medium">{t("signingPage.commodityIbanLabel")}:</span> {editableBillingInfo.iban || "—"}
                  </p>
                  <p>
                    <span className="font-medium">{t("signingPage.commodityAddress")}:</span>{" "}
                    {editableBillingInfo.billingAddressStreet
                      ? `${editableBillingInfo.billingAddressStreet} ${editableBillingInfo.billingAddressNumber}, ${editableBillingInfo.billingAddressZip} ${editableBillingInfo.billingAddressCity}`
                      : "—"}
                  </p>
                </div>
              )}

              {!showBillingEdit && (
                <>
                  {(!editableBillingInfo.billingEmail ||
                    !editableBillingInfo.iban ||
                    !editableBillingInfo.billingAddressStreet ||
                    !editableBillingInfo.billingAddressNumber ||
                    !editableBillingInfo.billingAddressZip ||
                    !editableBillingInfo.billingAddressCity) && (
                    <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded">
                      <p className="text-xs text-amber-700 flex items-center gap-1">
                        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                          <path
                            fillRule="evenodd"
                            d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                            clipRule="evenodd"
                          />
                        </svg>
                        {t("signingPage.commodityFillBillingFields")}
                      </p>
                    </div>
                  )}
                  {!ibanValid && (
                    <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded">
                      <p className="text-xs text-amber-700 flex items-center gap-1">
                        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                          <path
                            fillRule="evenodd"
                            d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                            clipRule="evenodd"
                          />
                        </svg>
                        {t("signingPage.commodityValidIbanRequired")}
                      </p>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
          <div>
            {/* Signature Section */}
            <div className="flex-1 p-4 flex  flex-col justify-between">
              <div className="space-y-4">
                {/* Terms Checkbox */}
                <div className="bg-white rounded-lg p-3 border border-gray-200">
                  <label className="flex items-start gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={hasAcceptedTerms}
                      onChange={(e) => handleAcceptTermsChange(e.target.checked)}
                      className="mt-0.5 w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                    />
                    <span className="text-xs text-gray-700">
                      {t("signingPage.commodityAcceptTerms")}
                      {!hasViewedAllPages && (
                        <span className="block text-amber-600 mt-1">{t("signingPage.commodityAutoScrollHint")}</span>
                      )}
                    </span>
                  </label>
                </div>

                {/* Signature Input */}
                <div className="bg-white rounded-lg p-4 border border-gray-200">
                  <p className="text-xs text-gray-600 mb-2">{t("signingPage.commodityTypeNameToSign")}</p>
                  <p className="font-semibold text-sm text-gray-800 mb-3">{signerName}</p>
                  <input
                    type="text"
                    value={signatureName}
                    onChange={(e) => setSignatureName(e.target.value)}
                    placeholder={t("signingPage.commoditySignNamePlaceholder")}
                    className={`w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                      !hasAcceptedTerms || !hasViewedAllPages
                        ? "bg-gray-50 border-gray-200"
                        : "bg-white border-gray-300"
                    }`}
                    disabled={isSignatureProcessing || !hasAcceptedTerms || !hasViewedAllPages}
                  />
                  {signatureName && signatureName !== signerName && (
                    <p className="text-xs text-red-600 mt-2">
                      {t("signingPage.commodityNameMustMatch")} <span className="font-semibold">{signerName}</span>
                    </p>
                  )}

                  <div className="mt-4">
                    <p className="text-xs font-medium text-gray-600 mb-2">
                      {t("signingPage.commoditySignatureHeading")}
                    </p>
                    <div className="flex border border-gray-200 rounded-lg overflow-hidden mb-2">
                      <button
                        onClick={() => setSignatureMode("type")}
                        className={`flex-1 py-1.5 text-xs font-medium transition-colors ${
                          signatureMode === "type"
                            ? "bg-primary-600 text-white"
                            : "bg-white text-gray-500 hover:bg-gray-50"
                        }`}
                      >
                        {t("signingPage.commoditySignatureModeType")}
                      </button>
                      <button
                        onClick={() => setSignatureMode("draw")}
                        className={`flex-1 py-1.5 text-xs font-medium transition-colors ${
                          signatureMode === "draw"
                            ? "bg-primary-600 text-white"
                            : "bg-white text-gray-500 hover:bg-gray-50"
                        }`}
                      >
                        {t("signingPage.commoditySignatureModeDraw")}
                      </button>
                      <button
                        onClick={() => setSignatureMode("upload")}
                        className={`flex-1 py-1.5 text-xs font-medium transition-colors ${
                          signatureMode === "upload"
                            ? "bg-primary-600 text-white"
                            : "bg-white text-gray-500 hover:bg-gray-50"
                        }`}
                      >
                        {t("signingPage.commoditySignatureModeUpload")}
                      </button>
                    </div>

                    {signatureMode === "type" ? (
                      <div className="h-12 bg-gray-50 border border-gray-200 rounded-lg flex items-center justify-center">
                        <span
                          className="text-primary-900 opacity-80"
                          style={{ fontFamily: "Georgia, serif", fontStyle: "italic", fontSize: "18px" }}
                        >
                          {signatureName || signerName || "—"}
                        </span>
                      </div>
                    ) : signatureMode === "draw" ? (
                      <div className="relative">
                        <canvas
                          ref={canvasRef}
                          width={258}
                          height={120}
                          onPointerDown={startDrawing}
                          onPointerMove={handleMove}
                          onPointerUp={stopDrawing}
                          onPointerCancel={stopDrawing}
                          className="border border-gray-200 rounded-lg bg-white cursor-crosshair w-full"
                          style={{ touchAction: "none" }}
                        />
                        {!canvasHasDrawing && (
                          <span className="absolute inset-0 flex items-center justify-center text-xs text-gray-300 pointer-events-none">
                            {t("signingPage.commodityDrawHere")}
                          </span>
                        )}
                        {canvasHasDrawing && (
                          <button
                            onClick={clearCanvas}
                            className="absolute bottom-1.5 right-2 text-[10px] text-gray-400 hover:text-gray-600"
                          >
                            {t("signingPage.commodityClearSignature")}
                          </button>
                        )}
                      </div>
                    ) : (
                      <div>
                        {uploadedSignature ? (
                          <div className="relative border border-gray-200 rounded-lg bg-gray-50 overflow-hidden">
                            <img
                              src={uploadedSignature}
                              alt={t("signingPage.commodityUploadedSignatureAlt")}
                              className="w-full h-[120px] object-contain p-2"
                            />
                            <button
                              onClick={() => setUploadedSignature(null)}
                              className="absolute top-1.5 right-2 text-[10px] text-gray-400 hover:text-gray-600 bg-white rounded px-1.5 py-0.5 border border-gray-200"
                            >
                              {t("signingPage.commodityUploadRemove")}
                            </button>
                          </div>
                        ) : (
                          <label
                            className="flex flex-col items-center justify-center h-[120px] border-2 border-dashed border-gray-300 rounded-lg bg-gray-50 cursor-pointer hover:bg-gray-100 hover:border-gray-400 transition-colors"
                            onDragOver={(e) => e.preventDefault()}
                            onDrop={(e) => {
                              e.preventDefault();
                              const file = e.dataTransfer.files[0];
                              if (file && (file.type === "image/png" || file.type === "image/jpeg")) {
                                const reader = new FileReader();
                                reader.onload = (ev) => setUploadedSignature(ev.target?.result as string);
                                reader.readAsDataURL(file);
                              }
                            }}
                          >
                            <input
                              type="file"
                              accept="image/png,image/jpeg"
                              className="hidden"
                              onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (file && (file.type === "image/png" || file.type === "image/jpeg")) {
                                  const reader = new FileReader();
                                  reader.onload = (ev) => setUploadedSignature(ev.target?.result as string);
                                  reader.readAsDataURL(file);
                                }
                              }}
                            />
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                              strokeWidth={1.5}
                              stroke="currentColor"
                              className="w-6 h-6 text-gray-400 mb-1.5"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5m-13.5-9L12 3m0 0 4.5 4.5M12 3v13.5"
                              />
                            </svg>
                            <span className="text-xs text-gray-500 text-center px-2">
                              {t("signingPage.commodityUploadDropHint")}
                            </span>
                          </label>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="mt-4">
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      {t("signingPage.commoditySignFunction")}
                    </label>
                    <input
                      type="text"
                      value={signeeFunction}
                      onChange={(e) => setSigneeFunction(e.target.value)}
                      placeholder={t("signingPage.commoditySignFunctionPlaceholder")}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      disabled={isSignatureProcessing}
                    />
                  </div>
                </div>
              </div>

              {/* Sign Button */}
              <button
                onClick={handleDigitalSignature}
                disabled={
                  !signatureName.trim() ||
                  !signeeFunction.trim() ||
                  !signatureValid ||
                  isSignatureProcessing ||
                  !hasAcceptedTerms ||
                  !hasViewedAllPages ||
                  (!ibanValid && !isAdmin) ||
                  !editableBillingInfo.billingEmail ||
                  !validateEmail(companyEmail) ||
                  signatureName !== signerName ||
                  !companyEmail
                }
                className={`w-full py-3 px-4 rounded-lg font-medium transition-all flex items-center justify-center gap-2 ${
                  !signatureName.trim() ||
                  !signeeFunction.trim() ||
                  !signatureValid ||
                  isSignatureProcessing ||
                  !hasAcceptedTerms ||
                  signatureName !== signerName ||
                  (!ibanValid && !isAdmin) ||
                  !editableBillingInfo.billingEmail ||
                  !hasViewedAllPages ||
                  !companyEmail
                    ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                    : "bg-primary-600 text-white hover:bg-primary-700 shadow-lg hover:shadow-xl"
                }`}
              >
                {isSignatureProcessing ? (
                  <>
                    <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    {t("signingPage.commoditySigning")}
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    {t("signingPage.commoditySignButton")}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
